import React, { useEffect } from 'react'
import { Grid } from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import useAttributes from 'utils/hooks/useAttributes'

import PostponedFormActionButtons from '../PostponedFormActionButtons'
import { MAX_POSTPONED_CONTENT_WIDTH, PostponedFormStepProps } from '..'

import {
  POSTPONED_FORM_ATTRIBUTES,
  useAuth,
} from 'utils/providers/AuthProvider'

import { validationSchema } from './config'
import { CatalogItem, ProfileAttribute, ReactSelect } from 'shared'
import { getCatalogOptions } from 'utils/helpers'
import {
  getAttrSelectValue,
  onChangeAttribute,
} from 'screens/Profile/infoBlocks/helpers'

interface ProfileFormValues {
  depth: ProfileAttribute | null
  undertone: ProfileAttribute | null
  skin_type: ProfileAttribute | null
  eye_color: ProfileAttribute | null
  hair_color: ProfileAttribute | null
  hair_type: ProfileAttribute | null
  hair_thickness: ProfileAttribute | null
  skin_characteristics: ProfileAttribute | null
  hair_characteristics: ProfileAttribute | null
}

const BeautyPreferencesForm = ({
  onStepChange,
  step,
  updateProfile,
}: PostponedFormStepProps) => {
  const { attributes, isLoading } = useAttributes(
    POSTPONED_FORM_ATTRIBUTES.beauty_preferences
  )

  const { user } = useAuth()

  const {
    depth,
    undertone,
    skin_type,
    skin_characteristics,
    eye_color,
    hair_color,
    hair_type,
    hair_thickness,
    hair_characteristics,
  } = attributes

  const { control, handleSubmit, reset } = useForm<ProfileFormValues>({
    //@ts-expect-error yup doesn't want to work with null but that's okay
    resolver: yupResolver(validationSchema),
  })

  useEffect(() => {
    const profile = user?.profile
    const getProfileAttrValue = (attr: string) => {
      return profile?.attributes.find(({ type }) => type === attr)
    }
    if (!profile) return

    reset({
      depth: getProfileAttrValue('depth'),
      undertone: getProfileAttrValue('undertone'),
      skin_type: getProfileAttrValue('skin_type'),
      hair_color: getProfileAttrValue('hair_color'),
      eye_color: getProfileAttrValue('eye_color'),
      hair_characteristics: getProfileAttrValue('hair_characteristics'),
      skin_characteristics: getProfileAttrValue('skin_characteristics'),
      hair_type: getProfileAttrValue('hair_type'),
      hair_thickness: getProfileAttrValue('hair_thickness'),
    })
  }, [user])

  const onSubmit = handleSubmit((data) => {
    const attributesWithValue = Object.values(data).filter((el) => el?.value)
    if (!attributesWithValue?.length) return
    updateProfile(attributesWithValue)
    onStepChange(step.id + 1)
  })

  if (isLoading) return null

  return (
    <>
      <Grid
        container
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{ maxWidth: MAX_POSTPONED_CONTENT_WIDTH }}
      >
        <Controller
          control={control}
          defaultValue={null}
          name="depth"
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="depth"
                options={getCatalogOptions(depth?.options)}
                value={value}
                label="Skin tone"
                placeholder="Select skin tone"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(depth, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          defaultValue={null}
          name="undertone"
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="undertone"
                options={getCatalogOptions(undertone?.options)}
                value={value}
                label="Undertone"
                placeholder="Select undertone"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(undertone, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          defaultValue={null}
          name="skin_type"
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="skin_type"
                options={getCatalogOptions(skin_type?.options)}
                value={value}
                label="Skin type"
                placeholder="Select skin type"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(skin_type, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          defaultValue={null}
          name="skin_characteristics"
          render={({ field, fieldState }) => {
            const isMultiple = skin_characteristics?.optionType === 'MULTIPLE'
            const value = getAttrSelectValue(
              isMultiple,
              field.value?.value as CatalogItem | CatalogItem[]
            )
            return (
              <ReactSelect
                isMulti={isMultiple}
                name="skin_characteristics"
                options={getCatalogOptions(skin_characteristics?.options)}
                value={value}
                label="Skin Characteristics (select all that apply)"
                placeholder="Select skin characteristics"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(skin_characteristics, e, field.onChange)
                }}
              />
            )
          }}
        />

        <Controller
          control={control}
          name="eye_color"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="eye_color"
                options={getCatalogOptions(eye_color?.options)}
                value={value}
                label="Eye color"
                placeholder="Select eye color"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(eye_color, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          defaultValue={null}
          name="hair_color"
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="hair_color"
                options={getCatalogOptions(hair_color?.options)}
                value={value}
                label="Hair Color"
                placeholder="Select hair color"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(hair_color, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          defaultValue={null}
          name="hair_type"
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="hair_type"
                options={getCatalogOptions(hair_type?.options)}
                value={value}
                label="Hair type"
                placeholder="Select hair type"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(hair_type, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          defaultValue={null}
          name="hair_thickness"
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="hair_thickness"
                options={getCatalogOptions(hair_thickness?.options)}
                value={value}
                label="Hair thickness"
                placeholder="Select hair thickness"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(hair_thickness, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="hair_characteristics"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const isMultiple = hair_characteristics?.optionType === 'MULTIPLE'
            const value = getAttrSelectValue(
              isMultiple,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                isMulti={isMultiple}
                name="hair_characteristics"
                options={getCatalogOptions(hair_characteristics?.options)}
                value={value}
                label="Hair characteristics (select all that apply)"
                placeholder="Select hair characteristics"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(hair_characteristics, e, field.onChange)
                }}
              />
            )
          }}
        />
      </Grid>
      <Grid container mt={5}>
        <PostponedFormActionButtons
          onBack={() => onStepChange(step.id - 1)}
          onSubmit={onSubmit}
        />
      </Grid>
    </>
  )
}

export default BeautyPreferencesForm
